import { IS_PRODUCTION } from '../../config/FruitfulEnvironment';

// --- Synctera ---

const syncteraBaseUrl = () => {
    return IS_PRODUCTION
        ? 'https://app.synctera.com'
        : 'https://app-sandbox.synctera.com';
};

export const syncteraPersonUrl = (syncteraPersonId: string) => {
    return `${syncteraBaseUrl()}/customers/personal/${syncteraPersonId}/accounts`;
};

export const syncteraAccountUrl = (syncteraAccountId: string) => {
    return `${syncteraBaseUrl()}/accounts/${syncteraAccountId}/transactions`;
};

export const syncteraPostedTransactionUrl = (transactionId: string) => {
    return `${syncteraBaseUrl()}/transactions/posted/${transactionId}`;
};

export const syncteraPendingTransactionUrl = (transactionId: string) => {
    return `${syncteraBaseUrl()}/transactions/pending/${transactionId}`;
};

// --- Apex Ascend ---

const apexSitesBaseUrl = () => {
    return IS_PRODUCTION
        ? 'https://sites.apexascend.com'
        : 'https://sites.uat.apexascend.com';
};

export const apexAccountUrl = (accountId: string) => {
    return `${apexSitesBaseUrl()}/accounts/${accountId}/summary`;
};

export const apexTransferUrl = (accountId: string, ictId: string) => {
    return `${apexSitesBaseUrl()}/transfer-processing/account/${accountId}/transfer/${ictId}`;
};

// --- Apex Rebalancer ---

export const APEX_REBALANCER_URL = IS_PRODUCTION
    ? 'https://portal.advisorarch.com/clients'
    : 'https://portal.sandbox.advisorarch.com/clients';

// --- Stripe ---

export const stripeCustomerDashboardUrl = (stripeCustomerId: string) => {
    const stripeBaseUrl = `https://dashboard.stripe.com${IS_PRODUCTION ? '' : '/test'}`;
    return `${stripeBaseUrl}/customers/${stripeCustomerId}`;
};

// --- LogRocket ---

const LOGROCKET_ORGANIZATION_ID = 'um7ghc';

export const logRocketSessionsUrl = ({
    userId,
    platform,
}: {
    userId: string;
    platform: 'ios' | 'web';
}): string => {
    const environment = IS_PRODUCTION ? 'production' : 'staging';
    const appPrefix = platform === 'ios' ? 'fruitful' : 'fruitful-rnw-web';
    const projectName = `${appPrefix}-app-${environment}`;
    const filterIntent = {
        type: 'all',
        children: [
            {
                level: 'session',
                type: 'userID',
                where: {
                    type: 'all',
                    children: [
                        {
                            type: 'userID',
                            operator: 'IS',
                            values: [userId],
                        },
                    ],
                },
            },
        ],
    };
    const queryParams = `filterIntent=${encodeURIComponent(encodeURIComponent(JSON.stringify(filterIntent)))}`;
    return `https://app.logrocket.com/${LOGROCKET_ORGANIZATION_ID}/${projectName}?${queryParams}`;
};

// --- Datadog LLM Observability ---

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * LLM Obs traces for a conversation. Newer turns tag `@session_id` (Mastra
 * threadId). Older CMM turns only have `@meta.metadata.threadId`, so the query
 * matches either. Traces list (not Sessions) so historical turns still appear.
 */
export const datadogLlmSessionUrl = ({
    sessionId,
    environment,
}: {
    sessionId: string;
    environment: 'staging' | 'production';
}): string => {
    const quotedId = `"${sessionId}"`;
    const query = [
        '@ml_app:llm-service',
        '@event_type:span',
        '@is_root_span:true',
        `env:${environment}`,
        `(@session_id:${quotedId} OR @meta.metadata.threadId:${quotedId})`,
    ].join(' ');
    const end = Date.now();
    const start = end - THIRTY_DAYS_MS;
    return (
        `https://app.datadoghq.com/llm/traces` +
        `?query=${encodeURIComponent(query)}` +
        `&agg_m=count` +
        `&agg_m_source=base` +
        `&agg_t=count` +
        `&fromUser=true` +
        `&start=${start}` +
        `&end=${end}` +
        `&paused=false`
    );
};

// --- Mixpanel ---
// Member-app Mixpanel project (fruitful-prod). Mixpanel.identify() uses Fruitful
// `user.id`; visitors are only tagged with a `visitorId` property.

const MIXPANEL_MEMBER_PROJECT_ID = '3006871';
const MIXPANEL_MEMBER_WORKSPACE_ID = '3525363';

/**
 * User profile for the Mixpanel id (`user.id` after identify,
 * otherwise visitorId on anonymous CMM sessions).
 */
export const mixpanelUserProfileUrl = (distinctId: string): string =>
    `https://mixpanel.com/project/${MIXPANEL_MEMBER_PROJECT_ID}` +
    `/view/${MIXPANEL_MEMBER_WORKSPACE_ID}` +
    `/app/profile#distinct_id=${encodeURIComponent(distinctId)}`;
